// HTML labels pinned to places on the globe (players and the Durable Object's home).

import type { PlayerInfo, ServerHome } from "../shared/protocol";

export interface Projector {
  project(lat: number, lon: number): { x: number; y: number; visible: boolean; facing: number };
}

interface LabelEntry {
  el: HTMLDivElement;
  lat: number;
  lon: number;
  priority: number;
  lastActive: number;
}

const MAX_LABELS = 9;

export class Labels {
  private entries = new Map<string, LabelEntry>();

  constructor(private readonly root: HTMLElement) {}

  private ensure(id: string, cls: string): LabelEntry {
    let e = this.entries.get(id);
    if (!e) {
      const el = document.createElement("div");
      el.className = `label ${cls}`;
      this.root.appendChild(el);
      e = { el, lat: 0, lon: 0, priority: 0, lastActive: 0 };
      this.entries.set(id, e);
    } else {
      e.el.className = `label ${cls}`;
    }
    return e;
  }

  sync(players: PlayerInfo[], youId: string | null, home: ServerHome | null): void {
    const keep = new Set<string>();
    if (home) {
      const e = this.ensure("__home", "home");
      e.el.innerHTML = `<b>⌂ DURABLE OBJECT</b><small>lives in ${escapeHtml(home.city)} · ${home.colo}</small>`;
      e.lat = home.lat;
      e.lon = home.lon;
      e.priority = 100;
      keep.add("__home");
    }
    for (const p of players) {
      const cls = p.id === youId ? "you" : p.ghost ? "ghost" : "player";
      const e = this.ensure(p.id, cls);
      const place = [p.city, p.country].filter(Boolean).join(", ");
      const rtt = p.rtt !== undefined ? ` · ${p.rtt} ms` : "";
      e.el.innerHTML = `<b style="color:hsl(${p.hue} 90% 75%)">${escapeHtml(p.name)}</b><small>${escapeHtml(place)} · via ${p.colo}${rtt}</small>`;
      e.lat = p.lat;
      e.lon = p.lon;
      e.priority = p.id === youId ? 90 : p.ghost ? 10 : 50;
      keep.add(p.id);
    }
    for (const [id, e] of this.entries) {
      if (!keep.has(id)) {
        e.el.remove();
        this.entries.delete(id);
      }
    }
  }

  /** Marks a label as recently active so it is shown even when crowded. */
  touch(id: string): void {
    const e = this.entries.get(id);
    if (e) e.lastActive = performance.now();
  }

  update(globe: Projector, hidden: boolean): void {
    const now = performance.now();
    const ranked = [...this.entries.values()]
      .map((e) => {
        const p = globe.project(e.lat, e.lon);
        const recent = now - e.lastActive < 4000 ? 40 : 0;
        return { e, p, score: e.priority + recent + p.facing * 10 };
      })
      .sort((a, b) => b.score - a.score);
    let shown = 0;
    const placed: { x: number; y: number }[] = [];
    for (const { e, p } of ranked) {
      let show = !hidden && p.visible && p.facing > 0.25 && shown < MAX_LABELS;
      // Avoid stacking labels on top of each other.
      if (show && placed.some((q) => Math.abs(q.x - p.x) < 90 && Math.abs(q.y - p.y) < 26)) show = false;
      if (show) {
        shown++;
        placed.push({ x: p.x, y: p.y });
        e.el.style.transform = `translate3d(${Math.round(p.x + 10)}px, ${Math.round(p.y - 8)}px, 0)`;
        e.el.style.opacity = String(Math.min(1, (p.facing - 0.25) * 3));
      } else {
        e.el.style.opacity = "0";
      }
    }
  }
}

export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}
